/**
 * ChecklistPanel — per-task checklist with progress tracking.
 *
 * Features:
 *   • Progress bar + percentage
 *   • Add new items inline
 *   • Check / uncheck items (every toggle is logged server-side in timeline)
 *   • Edit item text in-place
 *   • Delete items
 *   • Blocked-completion hint if any items remain unchecked
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../lib/api';

export default function ChecklistPanel({ appId, taskId, onProgressChange, readOnly = false, canManageItems, complianceTask, complianceSaving, onComplianceToggle }) {
  const api = useApi();

  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // Add-item state
  const [newText, setNewText]   = useState('');
  const [adding, setAdding]     = useState(false);

  // Inline-edit state
  const [editId, setEditId]     = useState(null);
  const [editText, setEditText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.listChecklist(appId, taskId);
      setItems(r.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, appId, taskId]);

  useEffect(() => { load(); }, [load]);

  // onProgressChange effect moved below compliance calculations so it can include them

  // canManageItems controls add / edit / delete (defaults to !readOnly if not supplied)
  const _canManage = canManageItems !== undefined ? canManageItems : !readOnly;

  const actionInProgress = useRef(false);

  // ── Toggle check/uncheck ─────────────────────────────────────────────────
  const toggle = async (item) => {
    if (actionInProgress.current) return;
    actionInProgress.current = true;
    const originalItems = items;
    const nextIsDone = item.is_done ? 0 : 1;
    // Optimistic update
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, is_done: nextIsDone } : i)));

    try {
      await api.updateChecklistItem(appId, taskId, item.id, { is_done: nextIsDone });
    } catch (err) {
      setItems(originalItems);
      setError(err.response?.data?.error || err.message);
    } finally {
      actionInProgress.current = false;
    }
  };

  // ── Add item ─────────────────────────────────────────────────────────────
  const handleAdd = async (e) => {
    e.preventDefault();
    const text = newText.trim();
    if (!text) return;
    setAdding(true);
    setError(null);
    try {
      const item = await api.addChecklistItem(appId, taskId, { text });
      setItems((prev) => [...prev, item]);
      setNewText('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setAdding(false);
    }
  };

  // ── Save inline edit ──────────────────────────────────────────────────────
  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text) return;
    try {
      const updated = await api.updateChecklistItem(appId, taskId, id, { text });
      setItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
      setEditId(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    const originalItems = items;
    // Optimistic update
    setItems((prev) => prev.filter((i) => i.id !== id));

    try {
      await api.removeChecklistItem(appId, taskId, id);
    } catch (err) {
      setItems(originalItems);
      setError(err.response?.data?.error || err.message);
    }
  };

  // ── Compliance items (deliverables + activities) ─────────────────────────
  const complianceGroups = complianceTask
    ? [
        { label: 'Activity',    items: complianceTask.major_activities || [], field: 'activities_checked' },
        { label: 'Deliverable', items: complianceTask.deliverables || [], field: 'deliverables_checked' },
      ].filter(g => g.items.length > 0)
    : [];
  const complianceItemsFlat = complianceGroups.flatMap(g =>
    g.items.map((text, idx) => ({
      text,
      field: g.field,
      idx,
      checked: (complianceTask[g.field] || []).includes(String(idx)),
      label: g.label,
    }))
  );
  const complianceDone = complianceItemsFlat.filter(i => i.checked).length;

  // ── Progress numbers (regular + compliance combined) ──────────────────────
  const total   = items.length + complianceItemsFlat.length;
  const done    = items.filter((i) => i.is_done).length + complianceDone;
  const pct     = total === 0 ? 0 : Math.round((done / total) * 100);
  const allDone = total > 0 && done === total;

  // Notify parent (TaskDetail) about progress including compliance items
  useEffect(() => {
    if (onProgressChange) {
      onProgressChange({ total, done });
    }
  }, [total, done, onProgressChange]);

  // Hide entire section while loading or when there are no items at all
  if (loading || total === 0) return null;

  return (
    <section className="task-section">
      <div className="checklist-header">
        <h2>
          Checklist
          {total > 0 && (
            <span className="muted small"> · {done}/{total}</span>
          )}
        </h2>
        {total > 0 && (
          <span className={`checklist-pct-badge ${allDone ? 'done' : ''}`}>{pct}%</span>
        )}
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div className="checklist-progress-wrap">
          <div
            className={`checklist-progress-bar ${allDone ? 'complete' : ''}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {/* Incomplete warning */}
      {total > 0 && !allDone && (
        <p className="checklist-incomplete-hint">
          ⚠ {total - done} item{total - done !== 1 ? 's' : ''} remaining —
          task cannot be marked Completed or Cancelled until all items are checked.
        </p>
      )}

      {error && <div className="banner banner-error" style={{ marginBottom: 8 }}>{error}</div>}

      {/* Item list */}
      {loading ? (
        <p className="muted">Loading checklist…</p>
      ) : (
        <ul className="checklist-list">
          {items.map((item) => (
            <li key={item.id} className={`checklist-item ${item.is_done ? 'is-done' : ''}`}>
              <input
                type="checkbox"
                checked={!!item.is_done}
                onChange={() => !readOnly && toggle(item)}
                disabled={readOnly}
                className="checklist-checkbox"
              />

              {editId === item.id ? (
                <span className="checklist-edit-wrap">
                  <input
                    className="form-input checklist-edit-input"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter')  saveEdit(item.id);
                      if (e.key === 'Escape') setEditId(null);
                    }}
                    autoFocus
                    maxLength={500}
                  />
                  <button className="btn-link-sm"       onClick={() => saveEdit(item.id)}>Save</button>
                  <button className="btn-link-sm muted" onClick={() => setEditId(null)}>Cancel</button>
                </span>
              ) : (
                <span className="checklist-text">{item.text}</span>
              )}

              {editId !== item.id && _canManage && (
                <span className="checklist-actions">
                  <button
                    className="btn-link-sm small"
                    onClick={() => { setEditId(item.id); setEditText(item.text); }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-link-sm danger small"
                    onClick={() => handleDelete(item.id)}
                  >
                    ✕
                  </button>
                </span>
              )}
            </li>
          ))}

          {/* Compliance deliverables & activities merged as checklist items */}
          {complianceItemsFlat.map((ci) => (
            <li key={`${ci.field}-${ci.idx}`} className={`checklist-item ${ci.checked ? 'is-done' : ''}`}>
              <input
                type="checkbox"
                checked={ci.checked}
                onChange={() => onComplianceToggle && onComplianceToggle(ci.field, ci.idx, ci.checked)}
                disabled={complianceSaving}
                className="checklist-checkbox"
              />
              <span className="checklist-text">
                {ci.text}
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>({ci.label})</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Add new item */}
      {_canManage && (
        <form onSubmit={handleAdd} className="checklist-add-row">
          <input
            type="text"
            className="form-input"
            placeholder="Add checklist item…"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            maxLength={500}
            disabled={adding}
          />
          <button
            type="submit"
            className="btn-secondary"
            disabled={adding || !newText.trim()}
          >
            {adding ? 'Adding…' : '+ Add'}
          </button>
        </form>
      )}
    </section>
  );
}
